import { getExtensionFromName } from "@/helpers/file-helpers";
import { IChat } from "@/interfaces/chat.interfaces";
import IContact from "@/interfaces/contact.interface";
import { IMessageInput } from "@/interfaces/message.interface";
import IUser from "@/interfaces/user.interface";
import { MessageType } from "@/types/message.types";

import { FormDataMediaKeys } from "./types";

export const buildMessageToSend = (
  chat: IChat,
  user: IUser,
  text: string,
  externalId: string,
  contactId: number,
  platformId: number,
  customerPlatformId: number
): IMessageInput => {
  const { name, phone1 } = user;
  const { contactExternalCode, contactName, conversationId } = chat;

  const timestampInSeconds = Math.floor(new Date().getTime() / 1000).toString();

  return {
    platformId: platformId,
    contactId,
    conversationId,
    customerPlatformId: customerPlatformId,
    externalId,
    fromId: externalId,
    fromName: name,
    fromPhoneNumber: phone1,
    toId: contactExternalCode,
    toName: contactName,
    toPhoneNumber: contactExternalCode,
    text,
    type: "text",
    attachment: null,
    attachmentCaption: null,
    midiaId: null,
    timestamp: timestampInSeconds,
    receivedDate: null,
    readDate: new Date().toISOString(),
    integrationDate: null,
    customerExternalId: externalId,
  };
};

export const buildMessageWithMediaId = (
  chat: IChat,
  user: IUser,
  text: string,
  mediaId: string | null,
  mediaType: MessageType,
  externalId: string | undefined,
  contactId: number,
  platformId: number,
  customerPlatformId: number
): IMessageInput | null => {
  if (!externalId) return null;
  const baseMessage = buildMessageToSend(
    chat,
    user,
    text,
    externalId,
    contactId,
    platformId,
    customerPlatformId
  );
  if (mediaId) {
    baseMessage.midiaId = mediaId;
    baseMessage.type = mediaType;
  }
  return baseMessage;
};

const formatPhoneNumber = (contact: IContact): string => {
  return `${contact.ddiMobile}${contact.dddMobile}${contact.mobilePhone}`;
};

const formatFullName = (contact: IContact): string => {
  return `${contact.firstName} ${contact.lastName}`.trim();
};

export const buildNewChat = (contact: IContact, platformId?: number): IChat => {
  return {
    platformId: platformId || 0,
    contactId: contact.id,
    conversationId: -1,
    contactName: formatFullName(contact),
    contactExternalCode: formatPhoneNumber(contact),
    rowDate: new Date().toISOString(),
    text: "",
    read: true,
    readDate: new Date().toISOString(),
    lastInterationDate: new Date().toISOString(),
    messages: {
      pageNumber: 1,
      pageSize: 10,
      items: [],
      hasNextPage: false,
      hasPreviousPage: false,
      totalCount: 0,
    },
  };
};

const removeExtensionFromName = (fileName: string): string => {
  return fileName.substring(0, fileName.lastIndexOf(".")) || fileName;
};

const getSimpleMediaType = (mimeType: string): string => {
  switch (mimeType.split("/")[0]) {
    case "image":
      return "image";
    case "video":
      return "video";
    case "audio":
      return "audio";
    case "application":
      return "document";
    default:
      return "unknown";
  }
};

const OPEN_CONVERSATION_KEY = "talk2buy.openConversation";

export type StoredOpenConversation = {
  conversationId: number;
  reportRow?: Record<string, unknown> | null;
};

export function rememberOpenConversation(value: StoredOpenConversation) {
  sessionStorage.setItem(OPEN_CONVERSATION_KEY, JSON.stringify(value));
}

export function readOpenConversation(): StoredOpenConversation | null {
  try {
    const raw = sessionStorage.getItem(OPEN_CONVERSATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredOpenConversation;
    return parsed?.conversationId > 0 ? parsed : null;
  } catch {
    return null;
  }
}

export function forgetOpenConversation() {
  sessionStorage.removeItem(OPEN_CONVERSATION_KEY);
}

const asText = (value: string | Date | null | undefined) => {
  if (!value) return "";
  return value instanceof Date ? value.toISOString() : String(value);
};

export function mapConversationDetailToChat(detail: {
  platformId: number;
  conversationId: number;
  contactId: number;
  contactName?: string | null;
  contactExternalCode?: string | null;
  messages?: IChat["messages"] | null;
  read?: boolean;
  rowDate?: string | Date | null;
  text?: string | null;
  readDate?: string | Date | null;
  lastReceivedDate?: string | Date | null;
  lastReadDate?: string | Date | null;
  lastInterationDate?: string | Date | null;
  firstAnswerDate?: string | Date | null;
  endDate?: string | Date | null;
  userId?: number | null;
  userName?: string | null;
  workflowId?: number | null;
  workflowName?: string | null;
  serviceQueueId?: number | null;
  serviceQueueName?: string | null;
}): IChat {
  return {
    platformId: detail.platformId,
    conversationId: detail.conversationId,
    contactId: detail.contactId,
    contactName: detail.contactName ?? "",
    contactExternalCode: detail.contactExternalCode ?? "",
    messages: detail.messages ?? {
      items: [],
      totalCount: 0,
      pageNumber: 1,
      pageSize: 0,
      hasNextPage: false,
      hasPreviousPage: false,
    },
    read: !!detail.read,
    rowDate: asText(detail.rowDate),
    text: detail.text ?? null,
    readDate: asText(detail.readDate),
    lastReceivedDate: detail.lastReceivedDate ? asText(detail.lastReceivedDate) : null,
    lastReadDate: detail.lastReadDate ? asText(detail.lastReadDate) : null,
    lastInterationDate: asText(detail.lastInterationDate),
    firstAnswerDate: detail.firstAnswerDate ? asText(detail.firstAnswerDate) : null,
    endDate: detail.endDate ? asText(detail.endDate) : null,
    userId: detail.userId ?? null,
    userName: detail.userName ?? null,
    workflowId: detail.workflowId ?? null,
    workflowName: detail.workflowName ?? null,
    serviceQueueId: detail.serviceQueueId ?? null,
    serviceQueueName: detail.serviceQueueName ?? null,
  };
}

export const buildMediaFormData = (
  selectedFile: File,
  customerExternalId: string | undefined
): FormData | undefined => {
  if (!customerExternalId) return;
  const formData = new FormData();
  const extension = getExtensionFromName(selectedFile.name);
  const mediaType = getSimpleMediaType(selectedFile.type);
  const nameWithoutExtension = removeExtensionFromName(selectedFile.name);

  formData.append(FormDataMediaKeys.FILE, selectedFile);
  formData.append(FormDataMediaKeys.EXTENSION, extension);
  formData.append(FormDataMediaKeys.TYPE, mediaType || "image");
  formData.append(FormDataMediaKeys.NAME, nameWithoutExtension);
  formData.append(FormDataMediaKeys.EXTERNAL_ID, "");

  return formData;
};
